import React from "react";
import { Link } from "react-router-dom";
import SellerAvatar from "@/components/SellerAvatar";

/**
 * VelocityLane — a racetrack where seller tokens move across the
 * screen at speeds proportional to their week-over-week growth.
 * Fastest grower laps the field. The whole thing is CSS animation
 * driven by per-token `animation-duration` derived from velocity_pct.
 *
 * Why this slaps: most marketplaces show a static leaderboard. Yours
 * literally accelerates the screen for whoever's growing fastest.
 * Sellers screenshot themselves moving. Buyers refresh just to watch.
 */
export default function VelocityLane({ data }) {
  const racers = data?.velocity || [];
  if (!racers.length) return null;

  // Map velocity_pct → lap duration. Cap at sane bounds so a 9999%
  // growth doesn't blur into the next decade.
  const fastest = Math.max(...racers.map((r) => r.velocity_pct), 1);
  const durFor = (v) => {
    const ratio = Math.max(0.15, Math.min(1, v / fastest));
    // 5s for fastest, 22s for slowest (linear interpolation)
    return 22 - ratio * 17;
  };

  return (
    <section
      className="relative bg-zinc-950 text-white py-14 sm:py-20 overflow-hidden border-y border-emerald-400/20"
      data-testid="beacon-velocity-lane"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_30%,rgba(16,185,129,0.18),transparent_55%)] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="overline text-emerald-300 mb-3">Velocity Lane</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">
              Fastest sellers on the grid
            </h2>
            <p className="mt-3 text-sm sm:text-base text-white/70 max-w-2xl">
              Token speed = week-over-week growth. Whoever's growing fastest
              physically moves fastest across your screen.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-emerald-300/80">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
            Live · {racers.length} racers
          </span>
        </div>

        <div className="space-y-3">
          {racers.slice(0, 6).map((r, i) => {
            const duration = durFor(r.velocity_pct);
            return (
              <div
                key={r.user_id}
                className="relative overflow-hidden rounded-full bg-white/5 border border-white/10 h-12"
                data-testid={`velocity-lane-${i}`}
              >
                {/* Lane number */}
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-mono text-white/40 z-10">
                  LANE {String(i + 1).padStart(2, "0")}
                </span>
                {/* Finish-line stripe */}
                <span className="absolute right-0 top-0 bottom-0 w-2 bg-[repeating-linear-gradient(45deg,rgba(255,255,255,0.4)_0_4px,transparent_4px_8px)]" />

                {/* The racing token */}
                <Link
                  to={`/seller/${r.user_id}`}
                  data-testid={`velocity-racer-${i}`}
                  className="absolute top-1/2 -translate-y-1/2 left-0 velocity-racer flex items-center gap-2 pl-12"
                  style={{ animationDuration: `${duration}s` }}
                >
                  <SellerAvatar
                    picture={r.picture}
                    name={r.username}
                    size={36}
                    className="ring-2 ring-emerald-300/60"
                  />
                  <span className="text-xs font-bold whitespace-nowrap bg-emerald-300/15 text-emerald-200 px-2 py-0.5 rounded-full border border-emerald-300/30 backdrop-blur-sm">
                    @{r.username} · +{r.velocity_pct}%
                  </span>
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
